/**
 * Operations Chat API – project-scoped multi-language chat.
 * Uses shared api client so X-Tenant-ID and Authorization are sent.
 */
import { api } from '../api';

/*
 * POR QUÉ LAS LECTURAS DE CHAT YA NO SE TRAGAN NADA
 *
 * Había aquí un `isGracefulChatReadFailure` que convertía red, `timeout` y 5xx
 * en `[]` o `null`. La intención era buena —que un chat caído no tumbe la
 * aplicación— pero el precio era que **un chat que falla se ve exactamente
 * igual que un chat que nadie ha usado**, y en un proyecto de campo «nadie ha
 * escrito nada» es una conclusión con la que se decide (T4, M1).
 *
 * Tumbar la aplicación no era el riesgo real: TanStack Query captura el
 * rechazo, deja `isError` a mano y `data` sin definir, que es lo que estos
 * consumidores ya hacían con el `null`. Y `OperationsChatPanel.js:131` tenía
 * escrito un camino de error para el canal que **no se ejecutaba nunca**,
 * porque el 5xx que lo habría activado se tragaba aquí.
 */

export const getMe = async () => {
  const { data } = await api.get('/chat/me');
  return data;
};

export const getOrCreateChannel = async (projectId) => {
  const { data } = await api.get('/chat/channels', { params: { project_id: projectId } });
  return data;
};

export const getMessages = async (channelId, options = {}) => {
  const params = {};
  if (options?.sinceId != null) params.since_id = options.sinceId;
  const { data } = await api.get(`/chat/channels/${channelId}/messages`, { params });
  return data;
};

/**
 * Cuántos mensajes sin leer y cuál es el último id — sin traer el historial.
 *
 * `sinceId` es la marca de agua del cliente (por navegador, en `localStorage`).
 * Ausente ≡ «nada leído todavía»: todo lo ajeno cuenta.
 */
export const getUnreadSummary = async (channelId, sinceId) => {
  const params = {};
  if (sinceId != null) params.since_id = sinceId;
  const { data } = await api.get(`/chat/channels/${channelId}/unread`, { params });
  return data;
};

/**
 * Post a message (text and/or files). Uses multipart/form-data.
 * @param {number} channelId
 * @param {string} body - Text body (can be empty if files are sent)
 * @param {File[]} [files] - Optional list of image/video files
 */
export const postMessage = async (channelId, body, files = []) => {
  const form = new FormData();
  form.append('body', body || '');
  (files || []).forEach((file) => form.append('files', file));
  const timeout = files?.length ? 60000 : 15000; // 60s when uploading files
  const { data } = await api.post(`/chat/channels/${channelId}/messages`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout,
  });
  return data;
};

export const setMyPreferredLanguage = async (preferredLanguage) => {
  const { data } = await api.patch('/chat/me/preferred-language', {
    preferred_language: preferredLanguage,
  });
  return data;
};

export const setUserPreferredLanguage = async (userId, preferredLanguage) => {
  const { data } = await api.patch(`/chat/users/${userId}/preferred-language`, {
    preferred_language: preferredLanguage,
  });
  return data;
};

/** Admin/Superadmin only: delete one message */
export const deleteMessage = async (channelId, messageId) => {
  await api.delete(`/chat/channels/${channelId}/messages/${messageId}`);
};

/** Admin/Superadmin only: clear all messages in the channel */
export const clearChannelMessages = async (channelId) => {
  await api.delete(`/chat/channels/${channelId}/messages`);
};
